// Mainly to provide common functions for the emarsys email subscription sections js file

// -------- Common Functions -------- //
// Extract the key-value pairs from the JSON object that match a specific format
// e.g. {single_1_id: "1", single_1_value: "1", single_1_overwrite: "false" etc...}
function getKeyValuePairWithSpecificFormat (jsonObject, format) {
  // Initialize an empty object to hold the matching key-value pairs
  let result = {};

  // Iterate over each key-value pair in the JSON object
  for (let key in jsonObject) {
    // If the key matches the pattern "text_number", add it to the result object
    if (key.indexOf(format) === 0) {
      result[key] = jsonObject[key];
    }
  }

  return result;
};

// Group the objects by their number
// Reference: https://apifox.com/apidoc/shared-39dd366b-a30e-4bab-a065-63aa9c7469c7
// e.g.
// emial: massageJsonIntoArrayOfObjectsByPrefix("", obj, type)
// from {single_1_id: "1", single_1_value: "1", single_1_overwrite: "false" etc...}
// to [{id: "1", "type": "single", "value": "1", "overwrite": "false"}, {id: "2", "type": "single", "value": "2", "overwrite": "false"}...]
// e.g.
// sms: massageJsonIntoArrayOfObjectsByPrefix("sms_", obj, type)
// from {sms_single_1_id: "1", sms_single_1_value: "1", sms_single_1_overwrite: "false" etc...}
// to [{id: "1", "type": "single", "value": "1", "overwrite": "false"}, {id: "2", "type": "single", "value": "2", "overwrite": "false"}...]
function massageJsonIntoArrayOfObjectsByPrefix(prefix, obj, type) {
  let result = {};

  for (let key in obj) {
    // Extract the number part of the key
    let numberPart = key.match(/\d+/)[0];

    // If the resulting object doesn't have a property for this number, create it
    if (!result[numberPart]) {
      result[numberPart] = {};
    }

    // Determine the new key name
    let newKey = key.replace(
      `${prefix ? prefix : ''}${type === "text" ? "subscription_text" : type}_` + `${numberPart}_`,
      ""
    );

    // Assign the value to the new key in the grouped object
    if (type === "multiple" && newKey === "value") {
      // Convert the value to an array of numbers
      result[numberPart][newKey] = obj[key]
        .split(",")
        .map((item) => Number(item));
    } else if (type === "single" && newKey === "value") {
      // Convert the value to a number
      result[numberPart][newKey] = Number(obj[key]);
    } else {
      result[numberPart][newKey] = obj[key];
    }

    // Convert the overwrite value from string to boolean
    if (newKey === "overwrite")
      result[numberPart]["overwrite"] = JSON.parse(obj[key]);
    // Add the type property
    result[numberPart]["type"] = type;
  }

  // Convert the resulting object to an array of objects
  let arrayResult = Object.values(result);

  // Remove empty objects
  arrayResult = arrayResult.filter((item) => item.id !== "");

  return arrayResult;
}

function massageJsonIntoArrayOfObjects (obj, type) {
  return massageJsonIntoArrayOfObjectsByPrefix("", obj, type);
}

function massageSMSJsonIntoArrayOfObjects  (obj, type) {
  return massageJsonIntoArrayOfObjectsByPrefix("sms_", obj, type);
}

function validateEmail (email) {
  var regex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

/**
 * @param {string} code like: +1 or +111
 * @param {string} phone like: 1234567890
 * @returns {boolean}
 */
function validatePhone (code, phone) {
  const text = `${code.substring(1)}${phone}`;
  return /^\d{8,15}$/.test(text);
}

function createTraceId () {
  const date = new Date();
  const year = `00${date?.getFullYear()}`.slice(-2);
  const month = `00${date.getMonth() + 1}`.slice(-2);
  const day = `00${date.getDay()}`.slice(-2);
  const hour = `00${date.getHours()}`.slice(-2);
  const minute = `00${date.getMinutes()}`.slice(-2);
  const second = `00${date.getSeconds()}`.slice(-2);
  const millSecond = `000${date.getMilliseconds()}`.slice(-3);
  const randomStr1 = `${Math.random()}`;
  const randomStr2 = `${Math.random()}`;
  return (
    year +
    month +
    day +
    hour +
    minute +
    second +
    millSecond +
    randomStr1.slice(-8) +
    randomStr2.slice(-9)
  );
};

function getContactListId (json) {
  return json.contact_list_id;
};

function getSMSContactListId (json) {
  return json.sms_contact_list_id;
};

function createFieldsArray (json) {
  // REMARKS: settingsJson is defined in the pdp-subscription-form.liquid section or other parent sections
  let getSingleObjects = getKeyValuePairWithSpecificFormat(
    json,
    "single_"
  );
  let getTextObjects = getKeyValuePairWithSpecificFormat(
    json,
    "subscription_text_"
  );
  let getMultipleObjects = getKeyValuePairWithSpecificFormat(
    json,
    "multiple_"
  );
  
  let singleObjectsArray = massageJsonIntoArrayOfObjects(
    getSingleObjects,
    "single"
  );
  let textObjectsArray = massageJsonIntoArrayOfObjects(getTextObjects, "text");
  let multipleObjectsArray = massageJsonIntoArrayOfObjects(
    getMultipleObjects,
    "multiple"
  );
  
  return [
    ...singleObjectsArray,
    ...textObjectsArray,
    ...multipleObjectsArray,
  ];
};

function createSMSFieldsArray (json) {
  // REMARKS: settingsJson is defined in the footer.liquid newsletter block or other parent sections
  let getSingleObjects = getKeyValuePairWithSpecificFormat(
    json,
    "sms_single_"
  );
  let getTextObjects = getKeyValuePairWithSpecificFormat(
    json,
    "sms_subscription_text_"
  );
  let getMultipleObjects = getKeyValuePairWithSpecificFormat(
    json,
    "sms_multiple_"
  );
  
  let singleObjectsArray = massageSMSJsonIntoArrayOfObjects(
    getSingleObjects,
    "single"
  );
  let textObjectsArray = massageSMSJsonIntoArrayOfObjects(getTextObjects, "text");
  let multipleObjectsArray = massageSMSJsonIntoArrayOfObjects(
    getMultipleObjects,
    "multiple"
  );
  
  return [
    ...singleObjectsArray,
    ...textObjectsArray,
    ...multipleObjectsArray,
  ];
};
// -------- Common Functions -------- //

// -------- Common Variables --------//
window.traceId = createTraceId();

// -------- UA Subscribe Tracking --------//
(function initSubscribeUaTracking() {
  if (window.__ecoflowSubscribeUaTrackingInitialized) {
    return;
  }

  window.__ecoflowSubscribeUaTrackingInitialized = true;
  window.__ecoflowSubscribeLastTrackingContext = null;

  const TRACKING_CONTEXT_EXPIRE_MS = 30000;
  const SUBSCRIBE_PREVIOUS_PAGE_KEY = "__ecoflowSubscribePreviousPage";
  const SUBSCRIBE_KEYWORDS = [
    "subscribe",
    "subscription",
    "newsletter",
    "sign up",
    "signup",
    "submit",
  ];

  function normalizeText(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
  }

  function getElementText(element) {
    if (!element) {
      return "";
    }

    return normalizeText(element.value || element.innerText || element.textContent);
  }

  function textIncludesSubscribeKeyword(value) {
    const normalized = normalizeText(value).toLowerCase();
    if (!normalized || normalized.indexOf("unsubscribe") > -1) {
      return false;
    }

    return SUBSCRIBE_KEYWORDS.some((keyword) => normalized.indexOf(keyword) > -1);
  }

  function getLabelText(input) {
    if (!input) {
      return "";
    }

    let label = input.closest("label");
    if (label) {
      return normalizeText(label.innerText || label.textContent);
    }

    if (input.id) {
      label = Array.from(document.querySelectorAll("label")).find(
        (item) => item.htmlFor === input.id
      );
    }

    if (!label) {
      label = input.parentElement?.querySelector("label");
    }

    return normalizeText(label?.innerText || label?.textContent);
  }

  function isElementVisible(element) {
    if (!element) {
      return false;
    }

    let current = element;
    while (current && current.nodeType === Node.ELEMENT_NODE) {
      const style = window.getComputedStyle(current);
      if (
        current.hidden ||
        current.getAttribute("aria-hidden") === "true" ||
        style.display === "none" ||
        style.visibility === "hidden" ||
        style.visibility === "collapse"
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return true;
  }

  function isCheckboxControlVisible(input) {
    if (isElementVisible(input)) {
      return true;
    }

    const visibleControl =
      input.closest("label") ||
      input.parentElement?.querySelector("label") ||
      input.parentElement;

    return isElementVisible(visibleControl);
  }

  function isPolicyCheckbox(input) {
    if (!input || input.type !== "checkbox") {
      return false;
    }

    if (!isCheckboxControlVisible(input)) {
      return false;
    }

    const datasetText = Object.keys(input.dataset || {})
      .map((key) => `${key} ${input.dataset[key]}`)
      .join(" ");
    const text = [
      input.id,
      input.name,
      input.className,
      input.value,
      input.getAttribute("aria-label"),
      input.getAttribute("data-subscribe-consent"),
      datasetText,
      getLabelText(input),
      input.closest("label")?.className,
      input.parentElement?.className,
    ]
      .join(" ")
      .toLowerCase();

    return /privacy|policy|terms|consent|opt[-_\s]?in|disclaimer|agreement/.test(text);
  }

  function getConsentPolicy(root) {
    const scope = root || document;
    let policyCheckboxes = Array.from(
      scope.querySelectorAll('input[type="checkbox"]')
    ).filter(isPolicyCheckbox);

    if (!policyCheckboxes.length && root?.closest) {
      const fallbackScope =
        root.closest("form") ||
        root.closest("section") ||
        root.closest("footer") ||
        document;

      policyCheckboxes = Array.from(
        fallbackScope.querySelectorAll('input[type="checkbox"]')
      ).filter(isPolicyCheckbox);
    }

    if (!policyCheckboxes.length) {
      return "None";
    }

    return policyCheckboxes.some((item) => item.checked) ? "Agree" : "Disagree";
  }

  function getSubscribeEmailValue(root, trigger) {
    const scopes = [
      root,
      trigger?.closest?.("form"),
      trigger?.closest?.("section"),
      trigger?.closest?.("footer"),
    ].filter(Boolean);

    const emailInputSelector = [
      'input[type="email"]',
      'input[name*="email"]',
      'input[id*="email"]',
      'input[class*="email"]',
    ].join(",");

    for (const scope of scopes) {
      const emailInput = Array.from(scope.querySelectorAll(emailInputSelector))
        .find((input) => input.value);

      if (emailInput) {
        return emailInput.value;
      }
    }

    return "";
  }

  function getProductName(root) {
    const productNames = [];
    const addProductName = (value) => {
      const productName = normalizeText(value);
      if (productName && !productNames.includes(productName)) {
        productNames.push(productName);
      }
    };

    if (root?.dataset) {
      addProductName(root.dataset.productName);
      addProductName(root.dataset.productTitle);
      addProductName(root.dataset.product);
    }

    if (window.meta?.product?.title) {
      addProductName(window.meta.product.title);
    }

    if (document.body?.dataset?.product) {
      addProductName(document.body.dataset.product);
    }

    if (root) {
      Array.from(root.querySelectorAll('input[type="checkbox"]:checked'))
        .filter((input) => !isPolicyCheckbox(input))
        .forEach((input) => {
          addProductName(input.dataset.productName);
          addProductName(getLabelText(input));
        });
    }

    return productNames.slice(0, 3).join("_");
  }

  function getTrackingRoot(trigger) {
    if (!trigger?.closest) {
      return document.body;
    }

    const explicitRoot = trigger.closest("[data-subscribe-tracking], [data-module-name]");
    if (explicitRoot) {
      return explicitRoot;
    }

    const formRoot = trigger.closest("form");
    if (formRoot) {
      return formRoot;
    }

    return (
      trigger.parentElement?.closest(
        [
          '[id*="subscription"]',
          '[id*="subscribe"]',
          '[id*="newsletter"]',
          '[class*="subscription"]',
          '[class*="subscribe"]',
          '[class*="newsletter"]',
          "section",
          "footer",
        ].join(",")
      ) || document.body
    );
  }

  function getModuleName(root) {
    return (
      normalizeText(root?.dataset?.moduleName) ||
      normalizeText(root?.dataset?.sectionType) ||
      normalizeText(root?.id) ||
      normalizeText(root?.classList?.[0]) ||
      "subscription"
    );
  }

  function getPageType() {
    return (
      normalizeText(window.theme?.pageType) ||
      normalizeText(window.ShopifyAnalytics?.meta?.page?.pageType) ||
      normalizeText(document.body?.className.match(/template-[^\s]+/)?.[0]) ||
      "unknown"
    );
  }

  function getPageTypeFromUrl(url) {
    if (!url) {
      return "unknown";
    }

    try {
      const pathname = new URL(url, window.location.origin).pathname;

      if (pathname === "/" || pathname === "") return "index";
      if (pathname.indexOf("/products/") === 0) return "product";
      if (pathname.indexOf("/collections/") === 0) return "collection";
      if (pathname.indexOf("/pages/") === 0) return "page";
      if (pathname.indexOf("/blogs/") === 0) {
        return pathname.split("/").filter(Boolean).length > 2 ? "article" : "blog";
      }
      if (pathname.indexOf("/cart") === 0) return "cart";
      if (pathname.indexOf("/search") === 0) return "search";
      if (pathname.indexOf("/account") === 0) return "customers/account";
      if (pathname.indexOf("/checkout") === 0) return "checkout";
    } catch (error) {
      return "unknown";
    }

    return "unknown";
  }

  function readPreviousPageInfo() {
    try {
      return JSON.parse(sessionStorage.getItem(SUBSCRIBE_PREVIOUS_PAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeCurrentPageInfo() {
    try {
      sessionStorage.setItem(
        SUBSCRIBE_PREVIOUS_PAGE_KEY,
        JSON.stringify({
          url: window.location.href,
          pageType: getPageType(),
        })
      );
    } catch (error) {}
  }

  function isSamePageUrl(urlA, urlB) {
    if (!urlA || !urlB) {
      return false;
    }

    try {
      return new URL(urlA, window.location.origin).href ===
        new URL(urlB, window.location.origin).href;
    } catch (error) {
      return urlA === urlB;
    }
  }

  const previousPageInfo = readPreviousPageInfo();
  const previousPageName = document.referrer || previousPageInfo.url || "";
  const previousPageType =
    isSamePageUrl(previousPageName, previousPageInfo.url)
      ? previousPageInfo.pageType || getPageTypeFromUrl(previousPageName)
      : getPageTypeFromUrl(previousPageName);
  writeCurrentPageInfo();

  function buildSubscribeTrackingParameters(eventName, options) {
    const root = options.root || getTrackingRoot(options.trigger);
    const buttonName =
      normalizeText(options.buttonName) ||
      getElementText(options.trigger) ||
      "submit";
    const parameters = {
      page_name: window.location.href,
      previous_page_name: previousPageName,
      button_name: buttonName,
      status: options.status || "success",
      module_name: normalizeText(options.moduleName) || getModuleName(root),
      entry_type: options.entryType || "fixed entry",
      entry_name:
        normalizeText(options.entryName) ||
        normalizeText(root?.dataset?.entryName) ||
        buttonName,
      entry_position: options.entryPosition || "none",
      page_type: getPageType(),
      previous_page_type: previousPageType,
    };

    if (eventName === "click_subscribe") {
      const productName =
        normalizeText(options.productName) || getProductName(root);
      parameters.tag = options.consentPolicy || getConsentPolicy(root);

      if (productName) {
        parameters.product_name = productName;
      }
    }

    return parameters;
  }

  function pushSubscribeUaEvent(eventName, options) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "uaEvent",
      event_name: eventName,
      ...buildSubscribeTrackingParameters(eventName, options || {}),
    });
  }

  function isSubscribeTrigger(trigger) {
    if (!trigger) {
      return false;
    }

    if (
      trigger.closest(
        "[data-subscribe-ignore], .email-subscription-popup__body-close-button"
      )
    ) {
      return false;
    }

    if (trigger.closest("[data-subscribe-tracking]")) {
      return true;
    }

    const relatedText = [
      getElementText(trigger),
      trigger.id,
      trigger.className,
      trigger.name,
      trigger.closest("form")?.id,
      trigger.closest("form")?.className,
      getTrackingRoot(trigger)?.id,
      getTrackingRoot(trigger)?.className,
    ].join(" ");

    return textIncludesSubscribeKeyword(relatedText);
  }

  document.addEventListener(
    "click",
    function handleSubscribeClick(event) {
      const trigger = event.target.closest(
        '[data-subscribe-tracking] button, [data-subscribe-tracking] input[type="button"], [data-subscribe-tracking] input[type="submit"], [data-subscribe-tracking] [role="button"]'
      );

      if (!isSubscribeTrigger(trigger)) {
        return;
      }

      const root = getTrackingRoot(trigger);
      const consentPolicy = getConsentPolicy(root);
      const isEmailValid = validateEmail(getSubscribeEmailValue(root, trigger));
      const context = {
        trigger,
        root,
        buttonName: getElementText(trigger),
        consentPolicy,
        moduleName: getModuleName(root),
        entryName:
          normalizeText(root?.dataset?.entryName) || getElementText(trigger),
        productName: getProductName(root),
        status: isEmailValid && consentPolicy === "Agree" ? "success" : "fail",
        ts: Date.now(),
      };

      window.__ecoflowSubscribeLastTrackingContext = context;
      pushSubscribeUaEvent("click_subscribe", context);
    },
    true
  );

  function getFetchUrl(input) {
    if (typeof input === "string") {
      return input;
    }

    return input?.url || "";
  }

  function getFetchMethod(input, init) {
    return String(init?.method || input?.method || "GET").toUpperCase();
  }

  function isSubscribeApiRequest(input, init) {
    const url = getFetchUrl(input);
    const emailSubscription = window.theme?.emailSubscription || {};
    const apiUrls = [
      emailSubscription.apiUrl,
      emailSubscription.apiUrlFooter,
    ].filter(Boolean);
    const method = getFetchMethod(input, init);

    return method === "POST" && apiUrls.some((apiUrl) => url.indexOf(apiUrl) === 0);
  }

  function isSubscribeApiSuccess(data) {
    return data?.message === "Success" || data?.code === "0";
  }

  if (window.fetch && !window.fetch.__ecoflowSubscribeUaWrapped) {
    const originalFetch = window.fetch;
    const wrappedFetch = function ecoflowSubscribeTrackingFetch(input, init) {
      const shouldTrackSubscribe = isSubscribeApiRequest(input, init);

      return originalFetch.apply(this, arguments).then((response) => {
        if (!shouldTrackSubscribe) {
          return response;
        }

        response
          .clone()
          .json()
          .then((data) => {
            if (!isSubscribeApiSuccess(data)) {
              return;
            }

            const lastContext = window.__ecoflowSubscribeLastTrackingContext;
            const context =
              lastContext &&
              Date.now() - lastContext.ts < TRACKING_CONTEXT_EXPIRE_MS
                ? lastContext
                : {};

            pushSubscribeUaEvent("subscribe_success", {
              ...context,
              status: "success",
            });
          })
          .catch(() => {});

        return response;
      });
    };

    wrappedFetch.__ecoflowSubscribeUaWrapped = true;
    window.fetch = wrappedFetch;
  }

  window.pushSubscribeUaEvent = pushSubscribeUaEvent;
  window.pushSubscribeGa4Event = pushSubscribeUaEvent;
})();
